import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFloorPlanZones, FloorPlanZone } from "./useFloorPlanZones";
import { toast } from "sonner";

/**
 * Hook for the "Toutes" floor-plan view: composes each zone as a draggable /
 * rotatable rectangle on a single global canvas.
 *
 * Persistence columns (on floor_plan_zones):
 *   - all_view_pos_x_px  (nullable)
 *   - all_view_pos_y_px  (nullable)
 *   - all_view_rotation  (0|90|180|270, default 0)
 *
 * Auto-layout: when all_view_pos_x_px is NULL for a zone, we synthesise a
 * flow position (left→right, wrap based on zone canvas widths) ordered by
 * order_index. The synthesised value is held in memory only and written to
 * the database on the first explicit save (no destructive backfill).
 */
export type ZoneRotation = 0 | 90 | 180 | 270;

export interface AllViewZone {
  id: string;
  name: string;
  color: string;
  orderIndex: number;
  // intrinsic (unrotated) canvas dimensions in px
  canvasWidth: number;
  canvasHeight: number;
  backgroundColor: string | null;
  // composed position on the global "Toutes" canvas
  posX: number;
  posY: number;
  rotation: ZoneRotation;
  /** true ⇒ position is auto-layout, not yet persisted */
  isAuto: boolean;
}

const FLOW_GAP = 24;
const FLOW_MAX_ROW_WIDTH = 3200;
const AUTO_SAVE_DELAY = 1500;

const DEFAULT_W = 1200;
const DEFAULT_H = 800;

const normaliseRotation = (r: number | null | undefined): ZoneRotation => {
  const v = Number(r ?? 0);
  return (v === 90 || v === 180 || v === 270 || v === 0) ? (v as ZoneRotation) : 0;
};

/** Compute flow auto-layout for zones missing a persisted position. */
export const computeAutoLayout = (zones: FloorPlanZone[]): Map<string, { x: number; y: number }> => {
  const map = new Map<string, { x: number; y: number }>();
  let cursorX = 0;
  let cursorY = 0;
  let rowHeight = 0;
  const ordered = [...zones].sort((a, b) => a.order_index - b.order_index);
  for (const z of ordered) {
    const w = z.canvas_width_px ?? DEFAULT_W;
    const h = z.canvas_height_px ?? DEFAULT_H;
    if (cursorX > 0 && cursorX + w > FLOW_MAX_ROW_WIDTH) {
      cursorX = 0;
      cursorY += rowHeight + FLOW_GAP;
      rowHeight = 0;
    }
    map.set(z.id, { x: cursorX, y: cursorY });
    cursorX += w + FLOW_GAP;
    rowHeight = Math.max(rowHeight, h);
  }
  return map;
};

/** Rotated bounding-box dimensions for a zone (intrinsic w/h + rotation). */
export const rotatedBox = (w: number, h: number, rotation: ZoneRotation) =>
  rotation === 90 || rotation === 270 ? { w: h, h: w } : { w, h };

/** Pure bounding box computation over composed zones. */
export interface BoundingBoxInput {
  posX: number;
  posY: number;
  canvasWidth: number;
  canvasHeight: number;
  rotation: ZoneRotation;
}
export const computeBoundingBox = (zones: BoundingBoxInput[]) => {
  if (zones.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const z of zones) {
    const { w, h } = rotatedBox(z.canvasWidth, z.canvasHeight, z.rotation);
    if (z.posX < minX) minX = z.posX;
    if (z.posY < minY) minY = z.posY;
    if (z.posX + w > maxX) maxX = z.posX + w;
    if (z.posY + h > maxY) maxY = z.posY + h;
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};

interface Options {
  establishmentId?: string;
  autoSave?: boolean;
}

export const useFloorPlanAllView = ({ establishmentId, autoSave = true }: Options) => {
  const { zones: rawZones, isLoading } = useFloorPlanZones({ establishmentId });

  const [overrides, setOverrides] = useState<Map<string, Partial<AllViewZone>>>(new Map());
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // history (undo/redo) of the overrides map
  const historyRef = useRef<Map<string, Partial<AllViewZone>>[]>([]);
  const futureRef = useRef<Map<string, Partial<AllViewZone>>[]>([]);
  const [, tick] = useState(0);
  const bump = () => tick(n => n + 1);

  const autoLayout = useMemo(() => computeAutoLayout(rawZones), [rawZones]);

  const zones: AllViewZone[] = useMemo(() => {
    return rawZones.map((z) => {
      const ov = overrides.get(z.id) ?? {};
      const hasPersistedPos = z.all_view_pos_x_px != null && z.all_view_pos_y_px != null;
      const auto = autoLayout.get(z.id) ?? { x: 0, y: 0 };
      const posX = ov.posX ?? (hasPersistedPos ? Number(z.all_view_pos_x_px) : auto.x);
      const posY = ov.posY ?? (hasPersistedPos ? Number(z.all_view_pos_y_px) : auto.y);
      const rotation = ov.rotation ?? normaliseRotation(z.all_view_rotation);
      return {
        id: z.id,
        name: z.name,
        color: z.color,
        orderIndex: z.order_index,
        canvasWidth: ov.canvasWidth ?? (z.canvas_width_px ?? DEFAULT_W),
        canvasHeight: ov.canvasHeight ?? (z.canvas_height_px ?? DEFAULT_H),
        backgroundColor: ov.backgroundColor ?? z.background_color ?? null,
        posX,
        posY,
        rotation,
        isAuto: !hasPersistedPos && !ov.posX && !ov.posY,
      };
    });
  }, [rawZones, overrides, autoLayout]);

  /** Global bounding box (in px) of all rotated zones. */
  const boundingBox = useMemo(() => computeBoundingBox(zones), [zones]);


  const snapshot = useCallback(() => {
    historyRef.current.push(new Map(overrides));
    if (historyRef.current.length > 50) historyRef.current.shift();
    futureRef.current = [];
    bump();
  }, [overrides]);

  const setOverride = useCallback((zoneId: string, patch: Partial<AllViewZone>) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(zoneId, { ...(next.get(zoneId) ?? {}), ...patch });
      return next;
    });
    setDirty((prev) => { const n = new Set(prev); n.add(zoneId); return n; });
  }, []);

  const updateZonePos = useCallback((zoneId: string, x: number, y: number) => {
    setOverride(zoneId, { posX: Math.round(x), posY: Math.round(y) });
  }, [setOverride]);

  const updateZoneRotation = useCallback((zoneId: string, rotation: ZoneRotation) => {
    setOverride(zoneId, { rotation });
  }, [setOverride]);

  const updateZoneCanvas = useCallback((zoneId: string, w: number, h: number) => {
    setOverride(zoneId, {
      canvasWidth: Math.max(200, Math.round(w)),
      canvasHeight: Math.max(200, Math.round(h)),
    });
  }, [setOverride]);

  const updateZoneBackground = useCallback((zoneId: string, color: string | null) => {
    setOverride(zoneId, { backgroundColor: color });
  }, [setOverride]);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current.pop()!;
    futureRef.current.push(new Map(overrides));
    setOverrides(prev);
    bump();
  }, [overrides]);

  const redo = useCallback(() => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current.pop()!;
    historyRef.current.push(new Map(overrides));
    setOverrides(next);
    bump();
  }, [overrides]);

  const save = useCallback(async () => {
    if (!establishmentId || dirty.size === 0) return true;
    setSaving(true);
    try {
      const ids = Array.from(dirty);
      const updates = ids.map((id) => {
        const z = zones.find(x => x.id === id);
        if (!z) return null;
        return supabase
          .from('floor_plan_zones')
          .update({
            all_view_pos_x_px: Math.round(z.posX),
            all_view_pos_y_px: Math.round(z.posY),
            all_view_rotation: z.rotation,
            canvas_width_px: Math.round(z.canvasWidth),
            canvas_height_px: Math.round(z.canvasHeight),
            background_color: z.backgroundColor,
          })
          .eq('id', id);
      }).filter(Boolean);
      const results = await Promise.all(updates as any[]);
      const errs = (results as any[]).filter((r: any) => r.error);
      if (errs.length > 0) throw errs[0].error;
      // Clear dirty + overrides (next render will read fresh persisted values)
      setDirty(new Set());
      setOverrides(new Map());
      return true;
    } catch (e) {
      console.error('[v2-all] save error', e);
      toast.error('Échec de la sauvegarde des zones');
      return false;
    } finally {
      setSaving(false);
    }
  }, [establishmentId, dirty, zones]);

  // Auto-save (debounced)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!autoSave || dirty.size === 0 || saving) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => { save(); }, AUTO_SAVE_DELAY);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [dirty, saving, autoSave, save]);

  return {
    zones,
    boundingBox,
    loading: isLoading,
    saving,
    dirty: dirty.size > 0,
    snapshot,
    updateZonePos,
    updateZoneRotation,
    updateZoneCanvas,
    updateZoneBackground,
    undo,
    redo,
    canUndo: historyRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    save,
  };
};
